import { useState } from 'react'
import { ChevronRight, Download, Save, X, RefreshCw, Loader2, Sparkles } from 'lucide-react'
import clsx from 'clsx'
import { DiffViewer } from '../DiffViewer'
import { hasChanges } from '../../utils/textDiff'

export type ElementType = 'background' | 'character' | 'title' | 'subtitle' | 'author' | 'publisher'

interface ElementConfig {
  icon: any
  label: string
  color: string
  dbKey: string
}

interface ElementPanelProps {
  element: ElementType
  config: ElementConfig
  isExpanded: boolean
  onToggle: () => void

  // Analysis data
  elementAnalysis: any
  originalPrompt: string  // Original analysis result
  currentPrompt: string   // Current editable prompt
  onPromptChange: (value: string) => void

  // Generation settings
  selectedModel: string
  onModelChange: (value: string) => void
  variation: number
  onVariationChange: (value: number) => void
  globalVariation: number

  // Image generation
  generatedImage: string | null
  isGenerating: boolean
  onRegenerate: () => void
  onDownload: () => void
  onSaveAsset: () => void
  onRemoveImage: () => void

  // AI Transform
  transformModel: string
  onTransformModelChange: (value: string) => void
  onTransform: () => void
  isTransforming: boolean
  transformVariation: number
  onTransformVariationChange: (value: number) => void

  // Reanalyze
  onReanalyze?: () => void

  // Background color for element (배경 외 요소의 배경색)
  backgroundColor?: string
  onBackgroundColorChange?: (value: string) => void

  // Models
  models: Array<{ id: string; name: string; description: string }>
  transformModels: Array<{ id: string; name: string; description: string }>
  variationOptions: Array<{ value: number; label: string }>
}

export function ElementPanel({
  element: _element,
  config,
  isExpanded,
  onToggle,
  elementAnalysis,
  originalPrompt,
  currentPrompt,
  onPromptChange,
  selectedModel,
  onModelChange,
  variation,
  onVariationChange,
  globalVariation,
  generatedImage,
  isGenerating,
  onRegenerate,
  onDownload,
  onSaveAsset,
  onRemoveImage,
  transformModel,
  onTransformModelChange,
  onTransform,
  isTransforming,
  transformVariation,
  onTransformVariationChange,
  onReanalyze,
  backgroundColor,
  onBackgroundColorChange,
  models,
  transformModels,
  variationOptions,
}: ElementPanelProps) {
  const Icon = config.icon
  const [activeTab, setActiveTab] = useState<'prompt' | 'analysis'>('prompt')
  const hasPromptChanges = hasChanges(originalPrompt, currentPrompt)

  return (
    <div
      className={clsx(
        'border rounded-lg overflow-hidden transition-all',
        isExpanded
          ? 'border-accent-gold ring-2 ring-accent-gold/20'
          : 'border-border-subtle hover:border-border-default'
      )}
    >
      {/* Compact Header - One Line */}
      <div className="flex items-center gap-4 p-4 bg-bg-secondary hover:bg-bg-elevated transition-colors">
        {/* Title with Icon */}
        <button
          onClick={onToggle}
          className="flex items-center gap-3 flex-shrink-0"
        >
          <div className={clsx('p-2 rounded-lg', config.color, 'bg-opacity-20')}>
            <Icon className={clsx('w-5 h-5', config.color.replace('bg-', 'text-'))} />
          </div>
          <div className="text-left">
            <h3 className="font-semibold">{config.label}</h3>
          </div>
          <ChevronRight className={clsx(
            'w-5 h-5 text-text-tertiary transition-transform',
            isExpanded && 'rotate-90'
          )} />
        </button>

        {/* Compact Settings - Only when expanded */}
        {isExpanded && (
          <>
            {/* Model Select */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <label className="text-xs text-text-tertiary whitespace-nowrap">모델:</label>
              <select
                value={selectedModel}
                onChange={(e) => onModelChange(e.target.value)}
                className="input text-sm py-1 px-2 w-40"
                onClick={(e) => e.stopPropagation()}
              >
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Background Color Select (배경 외 요소만) */}
            {backgroundColor && onBackgroundColorChange && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <label className="text-xs text-text-tertiary whitespace-nowrap">배경색:</label>
                <select
                  value={backgroundColor}
                  onChange={(e) => onBackgroundColorChange(e.target.value)}
                  className="input text-sm py-1 px-2 w-32"
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="auto">자동</option>
                  <option value="white">흰색</option>
                  <option value="black">검정</option>
                  <option value="gray">회색</option>
                  <option value="green">크로마키</option>
                </select>
              </div>
            )}

            {/* Variation Select */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <label className="text-xs text-text-tertiary whitespace-nowrap">변경율:</label>
              <select
                value={variation}
                onChange={(e) => onVariationChange(parseInt(e.target.value))}
                className="input text-sm py-1 px-2 w-32"
                onClick={(e) => e.stopPropagation()}
              >
                {variationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {globalVariation > 0 && (
                <span className="text-xs text-accent-gold">
                  (+{globalVariation}% 글로벌)
                </span>
              )}
            </div>

            {/* Generate Button (moved to header) */}
            <div className="ml-auto flex gap-2 flex-shrink-0">
              {/* Reanalyze Button */}
              {onReanalyze && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onReanalyze()
                  }}
                  className="btn btn-secondary text-sm py-1.5 px-4"
                  title="다른 프롬프트로 재분석"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  재분석
                </button>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onRegenerate()
                }}
                disabled={isGenerating || !elementAnalysis}
                className="btn btn-primary text-sm py-1.5 px-4"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    생성 중...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                    {generatedImage ? '재생성' : '이미지 생성'}
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-border-subtle">
          {/* Tabs */}
          <div className="flex border-b border-border-subtle bg-bg-secondary">
            <button
              onClick={() => setActiveTab('prompt')}
              className={clsx(
                'flex-1 px-4 py-3 text-sm font-medium transition-colors',
                activeTab === 'prompt'
                  ? 'bg-bg-primary text-accent-gold border-b-2 border-accent-gold'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
              )}
            >
              📝 재생성 프롬프트
              {hasPromptChanges && activeTab !== 'prompt' && (
                <span className="ml-2 inline-block w-2 h-2 bg-accent-gold rounded-full"></span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('analysis')}
              className={clsx(
                'flex-1 px-4 py-3 text-sm font-medium transition-colors',
                activeTab === 'analysis'
                  ? 'bg-bg-primary text-accent-gold border-b-2 border-accent-gold'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
              )}
            >
              📊 분석결과
            </button>
          </div>

          {/* Tab Content with Image on Right */}
          <div className="p-6">
            <div className="flex gap-6">
              {/* Left: Tab Content (flex-1) */}
              <div className="flex-1">
                {activeTab === 'prompt' ? (
                  /* Regeneration Prompt Tab */
                  <div className="space-y-4">
                    {/* Prompt Editor */}
                    <div>
                      <div className="flex items-center justify-between mb-2 gap-4">
                        {/* Left: Label + Status */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <label className="text-sm font-medium">프롬프트 편집</label>
                          {hasPromptChanges && (
                            <span className="text-xs bg-accent-gold/20 text-accent-gold px-2 py-0.5 rounded">
                              수정됨
                            </span>
                          )}
                        </div>

                        {/* Center: AI Transform Controls - Inline */}
                        <div className="flex items-center gap-2">
                          <select
                            value={transformModel}
                            onChange={(e) => onTransformModelChange(e.target.value)}
                            className="input text-xs py-1 px-2"
                            title="Text-to-Text 모델"
                          >
                            {transformModels.map((model) => (
                              <option key={model.id} value={model.id}>
                                {model.name}
                              </option>
                            ))}
                          </select>
                          <select
                            value={transformVariation}
                            onChange={(e) => onTransformVariationChange(parseInt(e.target.value))}
                            className="input text-xs py-1 px-2 w-32"
                            title="변형 강도"
                          >
                            {variationOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={onTransform}
                            disabled={isTransforming || transformVariation === 0}
                            className="btn btn-primary text-xs py-1 px-3 min-w-[100px]"
                          >
                            {isTransforming ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                변형 중
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-3 h-3 mr-1" />
                                AI 변형
                              </>
                            )}
                          </button>
                        </div>

                        {/* Right: Reset Button */}
                        <button
                          onClick={() => onPromptChange(originalPrompt)}
                          className="text-xs text-text-tertiary hover:text-accent-gold transition-colors flex-shrink-0"
                          title="원본 분석결과로 복원"
                        >
                          초기화
                        </button>
                      </div>

                      {/* Editable Prompt */}
                      <textarea
                        value={currentPrompt}
                        onChange={(e) => onPromptChange(e.target.value)}
                        placeholder="프롬프트를 입력하세요..."
                        className="input min-h-[200px] resize-y text-sm w-full"
                      />

                      {/* Diff Viewer - Show if changed */}
                      {hasPromptChanges && (
                        <div className="mt-3">
                          <p className="text-xs text-text-tertiary mb-2">변경사항:</p>
                          <div className="bg-bg-elevated rounded-lg p-4 border border-border-subtle max-h-32 overflow-y-auto">
                            <DiffViewer original={originalPrompt} modified={currentPrompt} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Analysis Result Tab */
                  <div className="space-y-4">
                    {elementAnalysis ? (
                      <div className="bg-bg-elevated rounded-lg p-4 border border-border-subtle">
                        <div className="space-y-3 text-sm text-text-secondary">
                          {elementAnalysis.description && (
                            <div>
                              <span className="text-text-tertiary font-medium">설명:</span>
                              <p className="mt-1">{elementAnalysis.description}</p>
                            </div>
                          )}
                          {elementAnalysis.style && (
                            <div>
                              <span className="text-text-tertiary font-medium">스타일:</span>
                              <p className="mt-1">{elementAnalysis.style}</p>
                            </div>
                          )}
                          {elementAnalysis.mood && (
                            <div>
                              <span className="text-text-tertiary font-medium">분위기:</span>
                              <p className="mt-1">{elementAnalysis.mood}</p>
                            </div>
                          )}
                          {elementAnalysis.colors && (
                            <div>
                              <span className="text-text-tertiary font-medium">색상:</span>
                              <p className="mt-1">{elementAnalysis.colors}</p>
                            </div>
                          )}
                          {elementAnalysis.composition && (
                            <div>
                              <span className="text-text-tertiary font-medium">구도:</span>
                              <p className="mt-1">{elementAnalysis.composition}</p>
                            </div>
                          )}
                          {elementAnalysis.lighting && (
                            <div>
                              <span className="text-text-tertiary font-medium">조명:</span>
                              <p className="mt-1">{elementAnalysis.lighting}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-text-tertiary">
                        <p>분석 결과가 없습니다</p>
                        <p className="text-xs mt-1">먼저 이미지를 분석해주세요</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right: Generated Image */}
              {generatedImage && (
                <div className="flex-shrink-0 w-64">
                  <div className="sticky top-6">
                    <p className="text-xs text-text-tertiary mb-2">생성된 이미지:</p>
                    <div className="relative aspect-[3/4] bg-bg-elevated rounded-lg overflow-hidden">
                      <img src={generatedImage} alt="Generated" className="w-full h-full object-cover" />
                      <div className="absolute top-2 right-2 flex flex-col gap-1">
                        <button
                          onClick={onDownload}
                          className="p-1.5 bg-black/70 rounded-lg hover:bg-black/90 transition-colors"
                          title="다운로드"
                        >
                          <Download className="w-3.5 h-3.5 text-white" />
                        </button>
                        <button
                          onClick={onSaveAsset}
                          className="p-1.5 bg-accent-gold/90 rounded-lg hover:bg-accent-gold transition-colors"
                          title="Asset으로 등록"
                        >
                          <Save className="w-3.5 h-3.5 text-white" />
                        </button>
                        <button
                          onClick={onRemoveImage}
                          className="p-1.5 bg-red-500/70 rounded-lg hover:bg-red-500/90 transition-colors"
                          title="삭제"
                        >
                          <X className="w-3.5 h-3.5 text-white" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
